import { useState, useEffect, useCallback } from "react"
import {
  Container,
  Typography,
  Box,
  Stack,
  Card,
  CardContent,
  Grid,
  Button,
  Chip,
  Input,
  IconButton,
  Tooltip,
  CircularProgress,
  FormControl,
  FormLabel,
  FormHelperText,
  Textarea,
  Select,
  Option,
  Sheet,
  Divider,
  Alert,
  Stepper,
  Step,
  StepIndicator,
  Switch,
  Radio,
  RadioGroup
} from "@mui/joy"
import {
  ArrowBack as BackIcon,
  ArrowForward as NextIcon,
  Send as SendIcon,
  Email as EmailIcon,
  ListAlt as ListIcon,
  Article as TemplateIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckIcon,
  Visibility as PreviewIcon
} from "@mui/icons-material"
import { useNavigate } from "react-router-dom"
import { toast } from "sonner"
import api from "../services/api"

// ---------------------------------------------------------------------------
// Tipos
// ---------------------------------------------------------------------------

interface ContactList {
  id: number
  name: string
  isEmailList: boolean
  provider?: string | null
  providerListId?: string | null
}

interface EmailTemplate {
  id: number
  name: string
  subject: string
  htmlContent: string
  textContent?: string | null
  status: string
  type: string
}

interface CampaignFormState {
  // Paso 1
  name: string
  subject: string
  fromEmail: string
  fromName: string
  replyTo: string

  // Paso 2
  contactListId: number | null

  // Paso 3
  templateId: number | null
  htmlContent: string
  textContent: string

  // Paso 4
  scheduleMode: "now" | "scheduled" | "draft"
  sendAt: string
  sendIntervalSeconds: number
  trackOpens: boolean
  trackClicks: boolean
}

const EMPTY_FORM: CampaignFormState = {
  name: "",
  subject: "",
  fromEmail: "",
  fromName: "",
  replyTo: "",
  contactListId: null,
  templateId: null,
  htmlContent: "",
  textContent: "",
  scheduleMode: "draft",
  sendAt: "",
  sendIntervalSeconds: 0,
  trackOpens: true,
  trackClicks: true
}

const STEPS = [
  { key: 1, title: "Datos basicos", icon: EmailIcon },
  { key: 2, title: "Lista de contactos", icon: ListIcon },
  { key: 3, title: "Plantilla", icon: TemplateIcon },
  { key: 4, title: "Programacion", icon: ScheduleIcon },
  { key: 5, title: "Confirmar", icon: CheckIcon }
]

// ---------------------------------------------------------------------------
// Componente
// ---------------------------------------------------------------------------

export default function EmailCampaignWizard() {
  const navigate = useNavigate()
  const [step, setStep] = useState(1)
  const [form, setForm] = useState<CampaignFormState>(EMPTY_FORM)
  const [submitting, setSubmitting] = useState(false)

  const [lists, setLists] = useState<ContactList[]>([])
  const [templates, setTemplates] = useState<EmailTemplate[]>([])
  const [loadingLists, setLoadingLists] = useState(false)
  const [loadingTemplates, setLoadingTemplates] = useState(false)

  // -------------------------------------------------------------------------
  // Carga
  // -------------------------------------------------------------------------

  const fetchLists = useCallback(async () => {
    setLoadingLists(true)
    try {
      const { data } = await api.get("/contact-lists/list")
      const all: ContactList[] = Array.isArray(data) ? data : (data?.records || [])
      setLists(all.filter(l => l.isEmailList))
    } catch {
      toast.error("Error cargando listas")
    } finally {
      setLoadingLists(false)
    }
  }, [])

  const fetchTemplates = useCallback(async () => {
    setLoadingTemplates(true)
    try {
      const { data } = await api.get("/email-templates", {
        params: { status: "active", type: "campaign", pageNumber: "1" }
      })
      setTemplates(data?.records || [])
    } catch {
      toast.error("Error cargando plantillas")
    } finally {
      setLoadingTemplates(false)
    }
  }, [])

  useEffect(() => {
    if (step === 2) fetchLists()
    if (step === 3) fetchTemplates()
  }, [step, fetchLists, fetchTemplates])

  // -------------------------------------------------------------------------
  // Validaciones por paso
  // -------------------------------------------------------------------------

  function canAdvance(currentStep: number): boolean {
    switch (currentStep) {
      case 1:
        return !!form.name && !!form.subject
      case 2:
        return !!form.contactListId
      case 3:
        return !!form.htmlContent
      case 4:
        return form.scheduleMode === "draft"
          || form.scheduleMode === "now"
          || (form.scheduleMode === "scheduled" && !!form.sendAt)
      default:
        return true
    }
  }

  // -------------------------------------------------------------------------
  // Submit final
  // -------------------------------------------------------------------------

  const handleSubmit = async () => {
    if (!form.name || !form.subject || !form.contactListId || !form.htmlContent) {
      toast.error("Faltan datos obligatorios")
      return
    }

    setSubmitting(true)
    try {
      const payload = {
        name: form.name,
        subject: form.subject,
        from_email: form.fromEmail || undefined,
        from_name: form.fromName || undefined,
        reply_to: form.replyTo || undefined,
        contactListId: form.contactListId,
        templateId: form.templateId || undefined,
        htmlContent: form.htmlContent,
        sendAt: form.scheduleMode === "scheduled" ? form.sendAt : null,
        launchNow: form.scheduleMode === "now",
        sendIntervalSeconds: form.sendIntervalSeconds,
        trackOpens: form.trackOpens,
        trackClicks: form.trackClicks
      }

      const { data } = await api.post("/email-campaigns/create-and-launch", payload)

      if (data?.success) {
        toast.success(data.message || "Campana creada")
        navigate("/email-marketing/campaigns")
      } else {
        toast.error(data?.message || "Error al crear campana")
      }
    } catch (err) {
      const ax = err as { response?: { data?: { message?: string } }; message?: string }
      toast.error(ax.response?.data?.message || ax.message || "Error al crear campana")
    } finally {
      setSubmitting(false)
    }
  }

  const handleApplyTemplate = (id: number) => {
    const tpl = templates.find(t => t.id === id)
    if (!tpl) return
    setForm({
      ...form,
      templateId: id,
      subject: form.subject || tpl.subject,
      htmlContent: tpl.htmlContent,
      textContent: tpl.textContent || ""
    })
    toast.success(`Plantilla "${tpl.name}" aplicada`)
  }

  // -------------------------------------------------------------------------
  // Render por paso
  // -------------------------------------------------------------------------

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      {/* Header */}
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <Tooltip title="Volver">
          <IconButton variant="outlined" onClick={() => navigate("/email-marketing/campaigns")}>
            <BackIcon />
          </IconButton>
        </Tooltip>
        <Typography level="h3" sx={{ fontWeight: 700 }}>
          Nueva campana de email
        </Typography>
      </Stack>

      {/* Stepper */}
      <Stepper sx={{ mb: 3 }}>
        {STEPS.map(s => {
          const Icon = s.icon
          const completed = step > s.key
          const active = step === s.key
          return (
            <Step
              key={s.key}
              orientation="vertical"
              indicator={
                <StepIndicator
                  variant={completed ? "solid" : active ? "solid" : "soft"}
                  color={completed || active ? "primary" : "neutral"}
                >
                  {completed ? <CheckIcon /> : <Icon />}
                </StepIndicator>
              }
            >
              <Typography
                level="body-sm"
                sx={{ fontWeight: active ? 700 : 500, color: active ? "text.primary" : "text.tertiary" }}
              >
                {s.title}
              </Typography>
            </Step>
          )
        })}
      </Stepper>

      {/* Step body */}
      <Card variant="outlined" sx={{ borderRadius: "lg", mb: 2 }}>
        <CardContent>
          {step === 1 && <StepBasicData form={form} setForm={setForm} />}
          {step === 2 && (
            <StepContactList
              lists={lists}
              loading={loadingLists}
              selectedId={form.contactListId}
              onSelect={id => setForm({ ...form, contactListId: id })}
              onRefresh={fetchLists}
            />
          )}
          {step === 3 && (
            <StepTemplate
              templates={templates}
              loading={loadingTemplates}
              form={form}
              setForm={setForm}
              onApply={handleApplyTemplate}
              onRefresh={fetchTemplates}
            />
          )}
          {step === 4 && <StepSchedule form={form} setForm={setForm} />}
          {step === 5 && <StepConfirm form={form} lists={lists} templates={templates} />}
        </CardContent>
      </Card>

      {/* Navigation buttons */}
      <Stack direction="row" justifyContent="space-between" spacing={1}>
        <Button
          variant="outlined"
          color="neutral"
          startDecorator={<BackIcon />}
          disabled={step <= 1 || submitting}
          onClick={() => setStep(s => Math.max(1, s - 1))}
        >
          Anterior
        </Button>

        {step < STEPS.length ? (
          <Button
            color="primary"
            endDecorator={<NextIcon />}
            disabled={!canAdvance(step) || submitting}
            onClick={() => setStep(s => Math.min(STEPS.length, s + 1))}
          >
            Siguiente
          </Button>
        ) : (
          <Button
            color="success"
            startDecorator={submitting ? <CircularProgress size="sm" /> : <SendIcon />}
            disabled={submitting}
            onClick={handleSubmit}
          >
            {submitting
              ? "Procesando..."
              : form.scheduleMode === "now"
              ? "Crear y lanzar"
              : form.scheduleMode === "scheduled"
              ? "Crear y programar"
              : "Crear borrador"}
          </Button>
        )}
      </Stack>
    </Container>
  )
}

// ===========================================================================
// Sub-componentes por paso
// ===========================================================================

function StepBasicData({ form, setForm }: { form: CampaignFormState; setForm: (f: CampaignFormState) => void }) {
  return (
    <Stack spacing={1.5}>
      <Typography level="title-md" sx={{ fontWeight: 600 }}>
        Datos basicos de la campana
      </Typography>
      <Grid container spacing={1.5}>
        <Grid xs={12}>
          <FormControl size="sm" required>
            <FormLabel>Nombre interno</FormLabel>
            <Input
              size="sm"
              placeholder="Newsletter Octubre"
              value={form.name}
              onChange={e => setForm({ ...form, name: e.target.value })}
            />
            <FormHelperText>Solo visible para tu equipo</FormHelperText>
          </FormControl>
        </Grid>
        <Grid xs={12}>
          <FormControl size="sm" required>
            <FormLabel>Asunto del email</FormLabel>
            <Input
              size="sm"
              placeholder="Hola {{nombre}}, novedades de octubre"
              value={form.subject}
              onChange={e => setForm({ ...form, subject: e.target.value })}
            />
          </FormControl>
        </Grid>
        <Grid xs={12} md={6}>
          <FormControl size="sm">
            <FormLabel>Email remitente</FormLabel>
            <Input
              size="sm"
              placeholder="marketing@chateam.ws"
              value={form.fromEmail}
              onChange={e => setForm({ ...form, fromEmail: e.target.value })}
            />
            <FormHelperText>Si vacio, usa el del provider</FormHelperText>
          </FormControl>
        </Grid>
        <Grid xs={12} md={6}>
          <FormControl size="sm">
            <FormLabel>Nombre remitente</FormLabel>
            <Input
              size="sm"
              placeholder="ChatEAM Marketing"
              value={form.fromName}
              onChange={e => setForm({ ...form, fromName: e.target.value })}
            />
          </FormControl>
        </Grid>
        <Grid xs={12}>
          <FormControl size="sm">
            <FormLabel>Reply-To (opcional)</FormLabel>
            <Input
              size="sm"
              placeholder="soporte@chateam.ws"
              value={form.replyTo}
              onChange={e => setForm({ ...form, replyTo: e.target.value })}
            />
          </FormControl>
        </Grid>
      </Grid>
    </Stack>
  )
}

function StepContactList({
  lists,
  loading,
  selectedId,
  onSelect,
  onRefresh
}: {
  lists: ContactList[]
  loading: boolean
  selectedId: number | null
  onSelect: (id: number) => void
  onRefresh: () => void
}) {
  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (lists.length === 0) {
    return (
      <Stack spacing={1.5} alignItems="center" sx={{ py: 4 }}>
        <ListIcon sx={{ fontSize: 48, opacity: 0.4 }} />
        <Typography level="title-md">Sin listas de email</Typography>
        <Typography level="body-sm" sx={{ color: "text.secondary" }}>
          Primero crea una lista marcada como "isEmailList"
        </Typography>
        <Button onClick={onRefresh} variant="outlined">Recargar</Button>
      </Stack>
    )
  }

  return (
    <Stack spacing={1.5}>
      <Typography level="title-md" sx={{ fontWeight: 600 }}>
        Selecciona la lista de contactos
      </Typography>
      <Grid container spacing={1.5}>
        {lists.map(list => {
          const isSelected = list.id === selectedId
          return (
            <Grid xs={12} md={6} key={list.id}>
              <Card
                variant={isSelected ? "solid" : "outlined"}
                color={isSelected ? "primary" : "neutral"}
                sx={{ cursor: "pointer", borderRadius: "lg" }}
                onClick={() => onSelect(list.id)}
              >
                <CardContent>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Radio checked={isSelected} variant="outlined" />
                    <Box sx={{ flex: 1 }}>
                      <Typography level="title-sm" sx={{ fontWeight: 600 }}>
                        {list.name}
                      </Typography>
                      <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }}>
                        {list.provider ? (
                          <Chip size="sm" variant="soft" color="primary">
                            {list.provider}
                          </Chip>
                        ) : (
                          <Chip size="sm" variant="soft" color="warning">
                            Sin sync
                          </Chip>
                        )}
                      </Stack>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          )
        })}
      </Grid>
    </Stack>
  )
}

function StepTemplate({
  templates,
  loading,
  form,
  setForm,
  onApply,
  onRefresh
}: {
  templates: EmailTemplate[]
  loading: boolean
  form: CampaignFormState
  setForm: (f: CampaignFormState) => void
  onApply: (id: number) => void
  onRefresh: () => void
}) {
  return (
    <Stack spacing={2}>
      <Typography level="title-md" sx={{ fontWeight: 600 }}>
        Plantilla y contenido
      </Typography>

      <Box>
        <Typography level="body-sm" sx={{ mb: 1 }}>
          Aplicar plantilla existente:
        </Typography>
        {loading ? (
          <CircularProgress size="sm" />
        ) : templates.length === 0 ? (
          <Alert color="warning" variant="soft">
            <Typography level="body-sm">
              No hay plantillas activas. Puedes editar el HTML directamente abajo.
            </Typography>
            <Button size="sm" variant="outlined" onClick={onRefresh}>
              Recargar
            </Button>
          </Alert>
        ) : (
          <Sheet variant="outlined" sx={{ borderRadius: "md", p: 1, maxHeight: 200, overflow: "auto" }}>
            <Stack spacing={0.5}>
              {templates.map(t => (
                <Stack
                  key={t.id}
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{
                    p: 1,
                    borderRadius: "sm",
                    bgcolor: form.templateId === t.id ? "primary.softBg" : "transparent",
                    "&:hover": { bgcolor: "neutral.softBg" }
                  }}
                >
                  <Typography level="body-sm" sx={{ flex: 1, fontWeight: 500 }}>
                    {t.name}
                  </Typography>
                  <Typography level="body-xs" sx={{ color: "text.tertiary", flex: 1 }}>
                    {t.subject}
                  </Typography>
                  <Button size="sm" variant="outlined" onClick={() => onApply(t.id)}>
                    Aplicar
                  </Button>
                </Stack>
              ))}
            </Stack>
          </Sheet>
        )}
      </Box>

      <Divider>O edita HTML directo</Divider>

      <FormControl size="sm" required>
        <FormLabel>HTML del email</FormLabel>
        <Textarea
          size="sm"
          minRows={12}
          maxRows={20}
          sx={{ fontFamily: "monospace", fontSize: 12 }}
          placeholder="<div>...</div>"
          value={form.htmlContent}
          onChange={e => setForm({ ...form, htmlContent: e.target.value })}
        />
      </FormControl>

      <FormControl size="sm">
        <FormLabel>Texto plano (opcional)</FormLabel>
        <Textarea
          size="sm"
          minRows={2}
          maxRows={4}
          value={form.textContent}
          onChange={e => setForm({ ...form, textContent: e.target.value })}
        />
      </FormControl>

      {form.htmlContent && (
        <Sheet variant="outlined" sx={{ borderRadius: "md", overflow: "hidden", height: 280 }}>
          <iframe title="preview" srcDoc={form.htmlContent} style={{ width: "100%", height: "100%", border: "none", backgroundColor: "#fff" }} />
        </Sheet>
      )}
    </Stack>
  )
}

function StepSchedule({ form, setForm }: { form: CampaignFormState; setForm: (f: CampaignFormState) => void }) {
  return (
    <Stack spacing={2}>
      <Typography level="title-md" sx={{ fontWeight: 600 }}>
        Programacion y opciones de envio
      </Typography>

      <FormControl>
        <FormLabel>Cuando enviar</FormLabel>
        <RadioGroup
          value={form.scheduleMode}
          onChange={e => setForm({ ...form, scheduleMode: e.target.value as CampaignFormState["scheduleMode"] })}
        >
          <Stack spacing={1}>
            <Radio value="draft" label="Guardar como borrador (no envia)" />
            <Radio value="now" label="Enviar ahora" />
            <Radio value="scheduled" label="Programar para fecha/hora" />
          </Stack>
        </RadioGroup>
      </FormControl>

      {form.scheduleMode === "scheduled" && (
        <FormControl size="sm" required>
          <FormLabel>Fecha y hora</FormLabel>
          <Input
            size="sm"
            type="datetime-local"
            value={form.sendAt}
            onChange={e => setForm({ ...form, sendAt: e.target.value })}
          />
          <FormHelperText>Tu zona horaria local</FormHelperText>
        </FormControl>
      )}

      <Divider>Opciones avanzadas</Divider>

      <FormControl size="sm">
        <FormLabel>Intervalo entre emails (segundos)</FormLabel>
        <Input
          size="sm"
          type="number"
          slotProps={{ input: { min: 0, max: 3600, step: 1 } }}
          value={form.sendIntervalSeconds}
          onChange={e => setForm({ ...form, sendIntervalSeconds: Number(e.target.value || 0) })}
        />
        <FormHelperText>
          0 = enviar via campana nativa del provider (rapido).
          {' >0 = enviar uno-a-uno con esta espera (mas lento, evita bloqueos).'}
        </FormHelperText>
      </FormControl>

      <Stack direction="row" spacing={2}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Switch
            checked={form.trackOpens}
            onChange={e => setForm({ ...form, trackOpens: e.target.checked })}
          />
          <Typography level="body-sm">Trackear aperturas</Typography>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          <Switch
            checked={form.trackClicks}
            onChange={e => setForm({ ...form, trackClicks: e.target.checked })}
          />
          <Typography level="body-sm">Trackear clicks</Typography>
        </Stack>
      </Stack>
    </Stack>
  )
}

function StepConfirm({
  form,
  lists,
  templates
}: {
  form: CampaignFormState
  lists: ContactList[]
  templates: EmailTemplate[]
}) {
  const list = lists.find(l => l.id === form.contactListId)
  const tpl = templates.find(t => t.id === form.templateId)

  return (
    <Stack spacing={2}>
      <Typography level="title-md" sx={{ fontWeight: 600 }}>
        Revisar antes de enviar
      </Typography>

      <Sheet variant="outlined" sx={{ p: 2, borderRadius: "md" }}>
        <Stack spacing={1}>
          <Stack direction="row" justifyContent="space-between">
            <Typography level="body-sm" sx={{ color: "text.tertiary" }}>Nombre</Typography>
            <Typography level="body-sm" sx={{ fontWeight: 600 }}>{form.name || "—"}</Typography>
          </Stack>
          <Stack direction="row" justifyContent="space-between">
            <Typography level="body-sm" sx={{ color: "text.tertiary" }}>Asunto</Typography>
            <Typography level="body-sm" sx={{ fontWeight: 600 }}>{form.subject || "—"}</Typography>
          </Stack>
          <Stack direction="row" justifyContent="space-between">
            <Typography level="body-sm" sx={{ color: "text.tertiary" }}>Remitente</Typography>
            <Typography level="body-sm">
              {form.fromName} {form.fromEmail && `<${form.fromEmail}>`}
            </Typography>
          </Stack>
          {form.replyTo && (
            <Stack direction="row" justifyContent="space-between">
              <Typography level="body-sm" sx={{ color: "text.tertiary" }}>Reply-To</Typography>
              <Typography level="body-sm">{form.replyTo}</Typography>
            </Stack>
          )}
          <Divider />
          <Stack direction="row" justifyContent="space-between">
            <Typography level="body-sm" sx={{ color: "text.tertiary" }}>Lista</Typography>
            <Typography level="body-sm" sx={{ fontWeight: 600 }}>
              {list?.name || "—"} {list?.provider && <Chip size="sm" sx={{ ml: 1 }}>{list.provider}</Chip>}
            </Typography>
          </Stack>
          {tpl && (
            <Stack direction="row" justifyContent="space-between">
              <Typography level="body-sm" sx={{ color: "text.tertiary" }}>Plantilla</Typography>
              <Typography level="body-sm">{tpl.name}</Typography>
            </Stack>
          )}
          <Divider />
          <Stack direction="row" justifyContent="space-between">
            <Typography level="body-sm" sx={{ color: "text.tertiary" }}>Modo</Typography>
            <Chip size="sm" color={form.scheduleMode === "now" ? "success" : form.scheduleMode === "scheduled" ? "primary" : "neutral"}>
              {form.scheduleMode === "now" ? "Enviar ahora" :
                form.scheduleMode === "scheduled" ? `Programado: ${form.sendAt}` :
                "Borrador"}
            </Chip>
          </Stack>
          <Stack direction="row" justifyContent="space-between">
            <Typography level="body-sm" sx={{ color: "text.tertiary" }}>Intervalo</Typography>
            <Typography level="body-sm">
              {form.sendIntervalSeconds === 0 ? "Provider nativo" : `${form.sendIntervalSeconds} segundos entre cada email`}
            </Typography>
          </Stack>
          <Stack direction="row" justifyContent="space-between">
            <Typography level="body-sm" sx={{ color: "text.tertiary" }}>Tracking</Typography>
            <Stack direction="row" spacing={0.5}>
              {form.trackOpens && <Chip size="sm" variant="soft">Aperturas</Chip>}
              {form.trackClicks && <Chip size="sm" variant="soft">Clicks</Chip>}
            </Stack>
          </Stack>
        </Stack>
      </Sheet>
    </Stack>
  )
}
